import { Injectable } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { UtilsService } from "@core/services/utils/utils.service";
import { UserDataService } from "@core/services/user-data/user-data.service";
import { OnlineCreditService } from "@core/services/online-credit/online-credit.service";
import { forkJoin, of } from "rxjs";
import {
  map,
  shareReplay,
  tap
} from 'rxjs/operators';
import { Observable } from "rxjs";
import { StepManagerService } from "@core/services/online-credit/step-manager.service";
import { FormFieldsListItem } from '@shared/models/modal-payment-form.model';
import { StoreService } from "../store/store.service";
import { FormSelectOption } from '@shared/models/form-select.model';
import { MaskUtilService } from "@core/services/utils/mask-util.service";

@Injectable()
export class PersonalDataFormService {

  private emailPattern =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  public actualAddress: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userDataService: UserDataService,
    private readonly onlineCreditService: OnlineCreditService,
    private readonly utilsService: UtilsService,
    private readonly store: StoreService
  ) {}

  getCheckPersonalDataForm() {
    return forkJoin(this.getPersonalData(), of(this.store.value.application)).pipe(
      tap(([personalData]) => this.store.changeIsOnlineFlag(!this.utilsService.isTruthy(personalData.isStopFactorFromCKS))),
      map(([personalData, application]): { form: FormGroup; fields: FormFieldsListItem[] } => {
        return {
          form: this.fb.group({
            fullName: [personalData.name, Validators.required],
            dockNumber: [`${personalData.docSer} ${personalData.docNum}`, Validators.required],
            snils: [application.snils || personalData.snils],
            regAddress: [personalData.regAddress, [Validators.required]],
            emailAddress: [application.emailAddress, [Validators.required, Validators.pattern(this.emailPattern)]]
          }),
          fields: [
            {controlName: 'fullName', title: 'ФИО', type: 'input', fakeDsbl: true, onlyMainForm: true},
            {
              controlName: 'dockNumber',
              title: 'Серия и номер паспорта',
              type: 'input',
              fakeDsbl: true,
              onlyMainForm: true
            },
            {
              controlName: 'regAddress',
              title: 'Адрес регистрации',
              type: 'input',
              fakeDsbl: true,
              onlyMainForm: true
            },
            {controlName: 'snils', title: 'СНИЛС *', type: 'input', textMask: {mask: MaskUtilService.snilsMask}},
            {controlName: 'emailAddress', title: 'Email *', type: 'input', maxLength: 25}
          ]
        };
      })
    );
  }

  getAdditionalPersonalDataForm() {
    return forkJoin([
      this.getPersonalData(),
      of(this.store.value.application),
      this.onlineCreditService.fetchMatrialStatuses(),
      this.onlineCreditService.fetchIncomeSpouse()
    ]).pipe(
      map(([personalData, application, matrial, incomeSpouse]) => {
        const snils = application.snils ? application.snils : Boolean(personalData.snils) ? personalData.snils : '';
        const email = application.emailAddress ? application.emailAddress : Boolean(personalData.email) ? personalData.email : '';
        const prevDoc = {
          prevDocser: application.prevDocser || personalData.prevDocser || '',
          prevDocnum: application.prevDocnum || personalData.prevDocnum || '',
          prevDocorgancode: application.prevDocorgancode || personalData.prevDocorgancode || null
        };
        const workExp = {
          month: +application.workExperience > 0 ? +application.workExperience % 12 : '',
          year: +application.workExperience > 0 ? (+application.workExperience / 12).toFixed(0) : ''
        };
        const actualAddress = application.actualAddress
          ? application.actualAddress
          : personalData.factAddress
          ? personalData.factAddress
          : '';
        return {
          form: this.fb.group({
            address: this.fb.group({
              actualAddress: [actualAddress, [Validators.required]],
              actualAddressUuid: [undefined, [Validators.required]],
              kladrCod: [undefined, [Validators.required]],
              snils: [snils, null],
              addressNotEqual: [false]
            }),

            prevDock: this.fb.group({
              prevDocser: [prevDoc.prevDocser, null],
              prevDocnum: [prevDoc.prevDocnum, null],
              prevDocorgancode: [prevDoc.prevDocorgancode, null]
            }),
            employer: this.fb.group({
              employersName: [application.employersName || null, Validators.required],
              employersInn: [application.employersInn || null, null],
              workPhone: [application.workPhone || null, null],
              income: [application.income || null, Validators.required]
            }),
            work: this.fb.group({
              workExperienceYear: [workExp.year, null],
              workExperienceMonth: [workExp.month, null]
            }),
            marital: this.fb.group({
              maritalStatus: [application.maritalStatus || null, Validators.required],
              spousesIncome: [application.spousesIncome || null, null],
              numberOfDependents: [
                application.hasOwnProperty('numberOfDependents') ? application.numberOfDependents : null,
                Validators.required
              ]
            }),
            rules: this.fb.group({
              agreeRules: [application.agreeRules ? application.agreeRules === 1 : true, [Validators.required, Validators.requiredTrue]],
              agreeInsurance: [
                application.agreeInsurance ? application.agreeInsurance === 1 : false,
                [Validators.required, Validators.requiredTrue]
              ],
              agreeDebt: [application.agreeDebt ? application.agreeDebt === 1 : false, [Validators.required, Validators.requiredTrue]]
            })
          }),
          fields: {
            address: [
              {
                controlName: 'actualAddress',
                title: 'Адрес фактического проживания *',
                autoHeight: true,
                tip: 'Поле обязательно',
                type: 'autocomplete',
                fieldType: 'address',
                fakeDsbl: true,
                options: []
              },
              {
                controlName: 'snils',
                title: 'СНИЛС *',
                type: 'input',
                isHidden: true,
                textMask: { mask: MaskUtilService.snilsMask }
              }
            ],
            prevDock: [
              {
                controlName: 'prevDocser',
                title: 'Серия паспорта',
                type: 'input',
                textMask: {mask: MaskUtilService.passportSerMask}
              },
              {
                controlName: 'prevDocnum',
                title: 'Номер паспорта',
                type: 'input',
                numberKeyboard: true,
                textMask: {mask: MaskUtilService.passportNumberMask}
              },
              {
                controlName: 'prevDocorgancode',
                title: 'Код выдавшего органа',
                type: 'input',
                numberKeyboard: true,
                textMask: {mask: MaskUtilService.docOrganCodeMask}
              }
            ],
            employer: [
              {
                controlName: 'employersName',
                title: 'Наименование работодателя *',
                tip: 'Поле обязательно',
                type: 'input'
              },
              {controlName: 'employersInn', title: 'ИНН Работодателя', type: 'input', numberKeyboard: true},
              {
                controlName: 'workPhone',
                title: 'Рабочий телефон',
                type: 'input',
                numberKeyboard: true,
                textMask: {mask: MaskUtilService.phoneMask}
              },
              {
                controlName: 'income',
                title: 'Доход (рублей) *',
                tip: 'Поле обязательно',
                type: 'amount',
                numberKeyboard: true,
                textMask: MaskUtilService.rubAmountMask
              }
            ],
            work: [
              {controlName: 'workExperienceYear', title: 'Лет', type: 'input', numberKeyboard: true},
              {controlName: 'workExperienceMonth', title: 'Месяцев', type: 'input', numberKeyboard: true}
            ],
            marital: [
              {
                controlName: 'maritalStatus',
                title: 'Семейное положение *',
                tip: 'Поле обязательно',
                type: 'select',
                options: this.getMaritalStatusesFormOptions(matrial)
              },
              {
                controlName: 'spousesIncome',
                title: 'Доход супруга(ги) *',
                tip: 'Поле обязательно',
                type: 'select',
                options: this.getIncomeSpouseFormOptions(incomeSpouse),
                isHidden: true
              },
              {
                controlName: 'numberOfDependents',
                title: 'Количество иждивенцев *',
                tip: '«Иждивенец – лицо, находящееся на Вашем материальном обеспечении и не имеющее иных источников дохода»',
                type: 'input',
                numberKeyboard: true,
                fakeDsbl: true
              }
            ]
          }
        };
      })
    );
  }

  private getPersonalData() {
    return forkJoin([this.userDataService.getClientInfo(), this.onlineCreditService.fetchClientAdditionalInfo()]).pipe(
      map(([clientInfo, additionalInfo]) => {
        return {...clientInfo, ...additionalInfo};
      }),
      tap((res) => (this.actualAddress = res.factAddress))
    );
  }

  private getMaritalStatusesFormOptions(maritalStatuses: any[]): FormSelectOption[] {
    return maritalStatuses.map((ms: any) => {
      return {value: ms.caption, caption: ms.caption, returnValue: ms.code};
    });
  }

  private getIncomeSpouseFormOptions(incomeSpouse: any[]): FormSelectOption[] {
    return incomeSpouse.map((is: any) => {
      return {value: is.caption, caption: is.caption, returnValue: is.code};
    });
  }
}
